import { useMemo, useState } from 'react';
import { CHROM_ORDER, type ParsedGenome, type Variant } from '../types';
import { filterVariants } from '../lib/stats';
import { copyToClipboard, variantNote, variantNoteFilename } from '../lib/note';
import { useVault } from '../lib/vault';
import { Explain } from './Explain';

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

interface Props {
  genome: ParsedGenome;
}

const PAGE_SIZE = 50;

function genotypeClass(g: string): string {
  if (g === '' || /[-.]/.test(g)) return 'nc';
  if (g.length === 2 && g[0] !== g[1]) return 'het';
  return 'hom';
}

export function VariantTable({ genome }: Props) {
  const [query, setQuery] = useState('');
  const [chrom, setChrom] = useState<string | 'all'>('all');
  const [page, setPage] = useState(0);
  const [copied, setCopied] = useState<string | null>(null);
  const { queueNote } = useVault();

  async function copyNote(v: Variant, key: string) {
    const body = variantNote(v, { date: today() });
    // Stage it in the vault as well as copying — the queue is how several
    // notes get exported together, and the clipboard only holds one.
    queueNote({
      kind: 'variant',
      title: v.rsid && v.rsid !== '.' ? v.rsid : `${v.chrom}:${v.pos}`,
      filename: variantNoteFilename(v),
      body,
    });
    const ok = await copyToClipboard(body);
    setCopied(ok ? key : `err:${key}`);
    window.setTimeout(() => setCopied(null), 1600);
  }

  const present = useMemo(() => {
    const set = new Set(genome.variants.map((v) => v.chrom));
    return CHROM_ORDER.filter((c) => set.has(c));
  }, [genome]);

  const filtered = useMemo(() => {
    const result = filterVariants(genome.variants, query, chrom);
    return result;
  }, [genome, query, chrom]);

  // Clamp page whenever the filter set shrinks.
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * PAGE_SIZE;
  const rows: Variant[] = filtered.slice(start, start + PAGE_SIZE);

  const isVcf = genome.format === 'vcf';

  return (
    <div className="card">
      <h2>
        Browse variants <span className="muted">· {filtered.length.toLocaleString()} match</span>
        <Explain id="browse" />
      </h2>
      <div className="controls">
        <input
          type="search"
          placeholder="Search rsID, position, genotype…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(0);
          }}
          aria-label="Search variants"
        />
        <select
          value={chrom}
          onChange={(e) => {
            setChrom(e.target.value);
            setPage(0);
          }}
          aria-label="Filter by chromosome"
        >
          <option value="all">All chromosomes</option>
          {present.map((c) => (
            <option key={c} value={c}>Chromosome {c}</option>
          ))}
        </select>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th>rsID</th>
              <th>Chr</th>
              <th>Position</th>
              {isVcf && <th>Ref</th>}
              {isVcf && <th>Alt</th>}
              <th>Genotype</th>
              <th aria-label="Actions"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((v, i) => {
              const key = `${v.chrom}:${v.pos}:${i}`;
              return (
                <tr key={key}>
                  <td className="mono">{v.rsid}</td>
                  <td>{v.chrom}</td>
                  <td className="mono">{v.pos.toLocaleString()}</td>
                  {isVcf && <td className="mono">{v.ref ?? ''}</td>}
                  {isVcf && <td className="mono">{v.alt ?? ''}</td>}
                  <td>
                    <span className={`chip ${genotypeClass(v.genotype)}`}>{v.genotype || '--'}</span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      className="note-btn"
                      title="Copy this variant as an Obsidian note"
                      onClick={() => copyNote(v, key)}
                    >
                      {copied === key ? '✓ copied' : copied === `err:${key}` ? 'copy failed' : '＋ note'}
                    </button>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={isVcf ? 7 : 5} style={{ color: 'var(--text-faint)', padding: '18px 10px' }}>
                  No variants match this filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="pager">
        <span className="count">
          {filtered.length === 0
            ? '0 results'
            : `Showing ${(start + 1).toLocaleString()}–${Math.min(start + PAGE_SIZE, filtered.length).toLocaleString()} of ${filtered.length.toLocaleString()}`}
        </span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={() => setPage(0)} disabled={safePage === 0}>« First</button>
          <button onClick={() => setPage(safePage - 1)} disabled={safePage === 0}>‹ Prev</button>
          <span className="count">Page {safePage + 1} / {pageCount}</span>
          <button onClick={() => setPage(safePage + 1)} disabled={safePage >= pageCount - 1}>Next ›</button>
          <button onClick={() => setPage(pageCount - 1)} disabled={safePage >= pageCount - 1}>Last »</button>
        </div>
      </div>
    </div>
  );
}
